import { Controller, useForm } from "react-hook-form";
import Button from "../../../../ui/Button/Button";
import { Pin } from "../../../../ui/Icon/Icon";
import Label from "../../../../ui/Label/Label";
import Popover from "../../../../ui/Popover/Popover";
import TextInput from "../../../../ui/TextInput/TextInput";
import styles from "./DomainPopover.module.scss";
import { useContext } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Feedback from "../../../../ui/Feedback/Feedback";
import validateUrl from "../../../../util/validate-url";
import PopoverContext from "../../../../ui/Popover/PopoverContext";

const schema = z.object({
  value: z.string().refine(
    (value) => {
      return validateUrl(value);
    },
    {
      message: "Please enter a valid URL",
    },
  ),
});

type DomainPopoverContentData = z.infer<typeof schema>;

function DomainPopoverContent({
  defaultValue,
  onSubmit,
}: {
  defaultValue: string;
  onSubmit: (data: DomainPopoverContentData) => void;
}) {
  const { setIsOpen } = useContext(PopoverContext);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<DomainPopoverContentData>({
    defaultValues: { value: defaultValue },
    resolver: zodResolver(schema),
  });

  return (
    <form
      className={styles.domainPopoverWrapper}
      onSubmit={handleSubmit((d) => {
        onSubmit(d);
        setIsOpen(false);
      })}
    >
      <div className={styles.domainPopoverInput}>
        <Label label="Your website URL">
          <Controller
            name="value"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextInput {...field} />
                {error?.message && <Feedback>{error.message}</Feedback>}
              </>
            )}
          />
        </Label>
      </div>

      <div className={styles.buttons}>
        <Button variant="primary" type="submit" disabled={!isDirty}>
          Save
        </Button>
      </div>
    </form>
  );
}

function DomainPopover({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Popover
      align="start"
      className={styles.domainPopover}
      content={
        <DomainPopoverContent
          defaultValue={value}
          onSubmit={(data) => onChange(data.value)}
        />
      }
    >
      <Button leftIcon={<Pin size="small" />} variant="outline">
        {value}
      </Button>
    </Popover>
  );
}

export default DomainPopover;
